import { Toaster as SonnerToaster, toast } from 'sonner';
import type { ComponentProps, ReactElement } from 'react';

import { useTheme } from '../theme/ThemeProvider.js';

type SonnerProps = ComponentProps<typeof SonnerToaster>;

/**
 * Sistema de toast da plataforma (via Sonner).
 *
 * **Padrão visual premium KmGestão** — card neutro (`bg-card`,
 * `border-border`, shadow) com acento colorido por tipo: borda
 * esquerda 4px + ícone no token semântico. Não usamos `richColors`
 * do Sonner (cores hard-coded fora do brand book); cada tipo mapeia
 * para uma CSS variable (`--success`, `--destructive`, `--warning`,
 * `--primary`) e segue light/dark automaticamente — zero hardcode.
 *
 * Tipos suportados (mesmo layout, só muda a cor do acento):
 * - `toast(msg)` — neutro (borda `--border`)
 * - `toast.success(msg)` — verde (token `--success`)
 * - `toast.error(msg)` — vermelho (token `--destructive`)
 * - `toast.warning(msg)` — âmbar (token `--warning`)
 * - `toast.info(msg)` — navy (token `--primary`) — identidade de marca
 *
 * Monte `<Toaster />` uma vez no shell do app, dentro do `ThemeProvider`.
 *
 * @example
 * // App root:
 * <ThemeProvider>
 *   <App />
 *   <Toaster />
 * </ThemeProvider>
 *
 * // Em qualquer componente:
 * import { toast } from '@kmgestao/ui';
 * toast.success('Paciente criado');
 * toast.error('Falha ao salvar', { description: 'Tente novamente.' });
 */
export function Toaster(props: SonnerProps): ReactElement {
  const { resolvedTheme } = useTheme();

  // Classes compartilhadas por todos os tipos. `group toast` no início
  // serve para que os modifiers `group-[.toast]:*` dos filhos (title,
  // description, etc.) funcionem — Sonner aplica `.toast` ao <li> que
  // envolve cada notificação. Left-border 4px sempre presente (cor
  // default = `--border`), sobrescrita por tipo abaixo.
  const baseToast = [
    'group toast',
    'group-[.toaster]:bg-card',
    'group-[.toaster]:text-card-foreground',
    'group-[.toaster]:border',
    'group-[.toaster]:border-border',
    'group-[.toaster]:border-l-4',
    'group-[.toaster]:shadow-lg',
    'group-[.toaster]:rounded-md',
  ].join(' ');

  return (
    <SonnerToaster
      theme={resolvedTheme}
      position="top-right"
      closeButton
      toastOptions={{
        classNames: {
          toast: baseToast,
          title: 'group-[.toast]:font-semibold group-[.toast]:text-card-foreground',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton:
            'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:rounded-md',
          cancelButton:
            'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:rounded-md',
          closeButton:
            'group-[.toast]:!bg-card group-[.toast]:!border-border group-[.toast]:!text-muted-foreground hover:group-[.toast]:!bg-muted',
          // Acento por tipo — borda esquerda + cor do ícone (Sonner
          // renderiza o ícone dentro de um `<div data-icon>`).
          success: 'group-[.toaster]:border-l-success [&>[data-icon]]:text-success',
          error: 'group-[.toaster]:border-l-destructive [&>[data-icon]]:text-destructive',
          warning: 'group-[.toaster]:border-l-warning [&>[data-icon]]:text-warning',
          info: 'group-[.toaster]:border-l-primary [&>[data-icon]]:text-primary',
        },
      }}
      {...props}
    />
  );
}

export { toast };
